import type { Product } from '@/admin/types/products'

const UNIT_IDENTITY_ROLE = 'unit_identity'

type IdentityAttributeLike = {
  code_name?: string
  display_name?: string
  configuration_data?: {
    value_stock?: { enabled?: boolean } | unknown
    identity?: boolean
    semantic_role?: string
    [key: string]: unknown
  }
}

type IdentityEntityLike = {
  metadata?: { identity_attributes?: unknown } | Record<string, unknown> | null
  attributes?: IdentityAttributeLike[]
} | null

function isStockEnabled(attr: IdentityAttributeLike): boolean {
  const stock = attr.configuration_data?.value_stock
  return Boolean(
    stock &&
      typeof stock === 'object' &&
      (stock as { enabled?: boolean }).enabled,
  )
}

function metadataIdentityCodes(metadata: unknown): string[] {
  const raw =
    metadata &&
    typeof metadata === 'object' &&
    'identity_attributes' in metadata
      ? (metadata as { identity_attributes?: unknown }).identity_attributes
      : undefined
  if (!Array.isArray(raw)) return []
  return raw
    .filter((code): code is string => typeof code === 'string')
    .map((code) => code.trim())
    .filter(Boolean)
}

export function stockBackedAttributeLabels(
  attributes: IdentityAttributeLike[] | undefined,
): string[] {
  return (attributes ?? [])
    .filter(isStockEnabled)
    .map((attr) => (attr.display_name || '').trim())
    .filter(Boolean)
}

/**
 * Labels for stock-backed *unit-identity* attributes only.
 *
 * Find a Product auto-lock and the identity search placeholder fire only for
 * the tenant's configured unit identifier. Other stocked holding fields keep
 * a normal catalogue search.
 */
export function stockBackedIdentityLabels(
  entity?: IdentityEntityLike,
): string[] {
  const codes = new Set(identityAttributeCodes(entity))
  if (codes.size === 0) return []
  return (entity?.attributes ?? [])
    .filter(
      (attr) =>
        Boolean(attr.code_name) &&
        codes.has(attr.code_name as string) &&
        isStockEnabled(attr),
    )
    .map((attr) => (attr.display_name || '').trim())
    .filter(Boolean)
}

/**
 * Tenant-configured unit-identity codes on the holding entity.
 *
 * Sources (union, metadata order first):
 * - ``metadata.identity_attributes`` (same key hosts already use)
 * - attributes tagged ``semantic_role: unit_identity`` or ``identity: true``
 *
 * Value-stock is a search/lock capability, not identity. Empty means
 * this tenant has no unit identifier - callers must not invent a
 * fallback code name.
 */
export function identityAttributeCodes(
  entity?: IdentityEntityLike,
): string[] {
  const fromMeta = metadataIdentityCodes(entity?.metadata)
  const fromAttrs = (entity?.attributes ?? [])
    .filter((attr) => {
      if (!attr.code_name) return false
      const cfg = attr.configuration_data ?? {}
      return (
        cfg.identity === true || cfg.semantic_role === UNIT_IDENTITY_ROLE
      )
    })
    .map((attr) => attr.code_name as string)
  return [...new Set([...fromMeta, ...fromAttrs])]
}

export function identityAttributeLabels(
  entity?: IdentityEntityLike,
): string[] {
  const codes = identityAttributeCodes(entity)
  if (codes.length === 0) return []
  const byCode = new Map(
    (entity?.attributes ?? [])
      .filter((attr) => typeof attr.code_name === 'string')
      .map((attr) => [attr.code_name as string, attr]),
  )
  return codes
    .map((code) => (byCode.get(code)?.display_name || '').trim())
    .filter(Boolean)
}

export function identityAttributeLabel(
  entity?: IdentityEntityLike,
): string {
  return identityAttributeLabels(entity).join(', ')
}

const IDENTITY_PRODUCT_DATA_KEYS = new Set(['sku', 'upc', 'ean'])

/**
 * Labels for a product that was resolved from a stocked value (e.g. a serial).
 *
 * A variant shows the parent line as the product and the variant axis as the
 * model. A standalone product has no model row.
 */
export const STOCK_STATUS_AVAILABLE = 'available'

export function isAvailableStockStatus(
  status: string | undefined | null,
): boolean {
  return status === STOCK_STATUS_AVAILABLE
}

type StockMatchLike = {
  status?: string | null
  product?: unknown
}

/** Exact resolve may return an in-use or retired row. Only available locks. */
export function lockableStockMatch<T extends StockMatchLike>(
  match: T | undefined,
): T | undefined {
  if (!match?.product) return undefined
  if (!isAvailableStockStatus(match.status)) return undefined
  return match
}

export function isTakenStockMatch(
  match: StockMatchLike | undefined,
): boolean {
  return Boolean(
    match?.product && match.status && !isAvailableStockStatus(match.status),
  )
}

export function lockedCatalogueLabels(product: Product): {
  productName: string
  modelName: string | null
} {
  if (product.product_type === 'VARIANT') {
    const productName =
      product.parent_product_name ||
      product.parent_product?.cached_short_name ||
      product.cached_short_name
    return {
      productName,
      modelName: modelLabel(product, productName),
    }
  }
  return { productName: product.cached_short_name, modelName: null }
}

function modelLabel(product: Product, productName: string): string {
  const short = (product.cached_short_name || '').trim()
  const prefix = `${productName} - `
  if (short.startsWith(prefix) && short.length > prefix.length) {
    return short.slice(prefix.length)
  }
  if (short && short !== productName) return short
  const data = product.product_data || {}
  for (const [key, value] of Object.entries(data)) {
    if (IDENTITY_PRODUCT_DATA_KEYS.has(key.toLowerCase())) continue
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return short || productName
}
